import { SensorType, SENSOR_TYPE_INFO, type SensorTypeInfo } from './sensors';
import {
  GasConcUnit,
  GAS_CONC_UNIT_OPTIONS,
  type GasConcUnitOption,
} from '../common/gas_conc_unit';
import type { CF_option } from './cf_lel';
import { SENSOR_TO_CF_OPTIONS } from './cf_lel';
import type { Ranges, BaseSensorConstraints } from '../common/constraints';
import {
  VOL_FOR_50_LEL_CH4_OPTIONS,
  VolFor50LelCh4,
  type VolFor50LelCh4Option,
} from './vol_for_50_lel_ch4';
import { GAS_CONC_SCALE } from '../common/constraints';

// ============================================================================
// Lite 센서별 제약조건 상수
// ============================================================================

// O2 센서 제약조건 - Lite
const O2_LITE_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 18000,
    ranges: [[0, 20900]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 19500,
    ranges: [
      [0, 20200],
      [21600, 30000],
    ],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 23500,
    ranges: [
      [0, 20200],
      [21600, 30000],
    ],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.VOL,
  twa_alarm: { default: 0, ranges: [[0, 0]], scale: 1 },
  stel_alarm: { default: 0, ranges: [[0, 0]], scale: 1 },
  stel_period: { default: 0, ranges: [[0, 0]], unit: 'min' },
  twa_period: { default: 0, ranges: [[0, 0]], unit: 'hours' },
  auto_fresh: 0,
};

// CO 센서 제약조건 - Lite
const CO_LITE_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 100000,
    ranges: [[10000, 2000000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 30000,
    ranges: [[10000, 2000000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 100000,
    ranges: [[10000, 2000000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 30000,
    ranges: [[10000, 2000000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 200000,
    ranges: [[10000, 2000000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// H2S 센서 제약조건 - Lite
const H2S_LITE_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 25000,
    ranges: [[1000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 10000,
    ranges: [[1000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 15000,
    ranges: [[1000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 10000,
    ranges: [[1000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 15000,
    ranges: [[1000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// O2LF (Low Flow O2) 센서 제약조건 - Lite
const O2LF_LITE_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 18000,
    ranges: [[0, 20900]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 19500,
    ranges: [
      [0, 20200],
      [21600, 30000],
    ],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 23500,
    ranges: [
      [0, 20200],
      [21600, 30000],
    ],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.VOL,
  twa_alarm: { default: 0, ranges: [[0, 100]], scale: GAS_CONC_SCALE },
  stel_alarm: { default: 0, ranges: [[0, 100]], scale: GAS_CONC_SCALE },
  stel_period: { default: 0, ranges: [[0, 0]], unit: 'min' },
  twa_period: { default: 0, ranges: [[0, 0]], unit: 'hours' },
  auto_fresh: 0,
};

// CO2 센서 제약조건 (PPM) - Lite
const CO2_LITE_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 25000000,
    ranges: [[5000000, 50000000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 5000000,
    ranges: [[500000, 50000000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 15000000,
    ranges: [[500000, 50000000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: {
    default: 5000000,
    ranges: [[500000, 50000000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 15000000,
    ranges: [[500000, 50000000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// CO2 센서 제약조건 (%VOL) - Lite
const CO2_VOL_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 2500,
    ranges: [[500, 5000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: {
    default: 500,
    ranges: [[50, 5000]],
    scale: GAS_CONC_SCALE,
  },
  high_alarm: {
    default: 1500,
    ranges: [[50, 5000]],
    scale: GAS_CONC_SCALE,
  },
  gas_conc_unit: GasConcUnit.VOL,
  twa_alarm: {
    default: 500,
    ranges: [[50, 5000]],
    scale: GAS_CONC_SCALE,
  },
  stel_alarm: {
    default: 1500,
    ranges: [[50, 5000]],
    scale: GAS_CONC_SCALE,
  },
  stel_period: { default: 15, ranges: [[5, 15]], unit: 'min' },
  twa_period: { default: 8, ranges: [[4, 8]], unit: 'hours' },
  auto_fresh: 0,
};

// LEL 센서 공통 제약조건 (LEL_LPC, LEL_1Series, LEL_IR)
const LEL_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 180, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: {
    default: 50000,
    ranges: [[1000, 100000]],
    scale: GAS_CONC_SCALE,
  },
  low_alarm: { default: 10000, ranges: [[5000, 60000]], scale: GAS_CONC_SCALE },
  high_alarm: { default: 20000, ranges: [[5000, 60000]], scale: GAS_CONC_SCALE },
  gas_conc_unit: GasConcUnit.LEL,
  twa_alarm: { default: 0, ranges: [[0, 0]], scale: 1 },
  stel_alarm: { default: 0, ranges: [[0, 0]], scale: 1 },
  stel_period: { default: 0, ranges: [[0, 0]], unit: 'min' },
  twa_period: { default: 0, ranges: [[0, 0]], unit: 'hours' },
  auto_fresh: 0,
};

// NO_SENSOR 제약조건
const NO_SENSOR_CONSTRAINTS: BaseSensorConstraints = {
  calibration_interval: { default: 180, ranges: [[0, 730]], unit: 'days' },
  bump_interval: { default: 0, ranges: [[0, 365]], unit: 'days' },
  calibration_gas_conc: { default: 0, ranges: [[0, 1]], scale: 1 },
  low_alarm: { default: 0, ranges: [[0, 1]], scale: 1 },
  high_alarm: { default: 0, ranges: [[0, 1]], scale: 1 },
  gas_conc_unit: GasConcUnit.PPM,
  twa_alarm: { default: 0, ranges: [[0, 1]], scale: 1 },
  stel_alarm: { default: 0, ranges: [[0, 1]], scale: 1 },
  stel_period: { default: 0, ranges: [[0, 0]], unit: 'min' },
  twa_period: { default: 0, ranges: [[0, 0]], unit: 'hours' },
  auto_fresh: 0,
};

// Custom LEL 제약조건 (대부분의 센서용)
const CUSTOM_LEL_STANDARD: Ranges = {
  default: GAS_CONC_SCALE,
  ranges: [[10, 15000]],
  scale: GAS_CONC_SCALE,
};

// Custom LEL 제약조건 (LEL_IR, NO_SENSOR용)
const CUSTOM_LEL_IR_VARIANT: Ranges = {
  default: GAS_CONC_SCALE,
  ranges: [[100, 15000]],
  scale: GAS_CONC_SCALE,
};

// Lite 센서 기능 플래그
interface SensorFeatureFlags {
  has_twa_stel: boolean;
  enable_unit_selection: boolean;
  enable_auto_fresh: boolean; // CH4-IR, CO2-IR은 항상 false
  enable_correction_factor: boolean;
  custom_lel_editable: boolean;
  enable_bump_test_threshold: boolean; // O2, O2LF는 false (40으로 고정)
}

// Lite 센서 제약조건 (BaseSensorConstraints 확장)
// Constraints는 범위(Ranges), 선택 옵션(options), 기능 플래그(features)만 포함
interface SensorConstraints extends BaseSensorConstraints {
  custom_lel?: Ranges;
  lel_CF_Index?: CF_option[];
  vol_for_50_lel_ch4?: VolFor50LelCh4Option[];
  unit?: GasConcUnitOption[];
  bump_test_threshold?: Ranges;
  features?: SensorFeatureFlags;
}

// 센서별 제약조건 매핑
export const SENSOR_CONSTRAINTS: Record<SensorType, SensorConstraints> = {
  [SensorType.O2]: {
    ...O2_LITE_CONSTRAINTS,
    custom_lel: CUSTOM_LEL_STANDARD,
    vol_for_50_lel_ch4: [],
    lel_CF_Index: SENSOR_TO_CF_OPTIONS[SensorType.O2],
    bump_test_threshold: { default: 40, ranges: [[40, 40]], scale: 1, unit: '%' },
    features: {
      has_twa_stel: false,
      enable_unit_selection: false,
      enable_auto_fresh: true,
      enable_correction_factor: false,
      custom_lel_editable: false,
      enable_bump_test_threshold: false,
    },
  },
  [SensorType.CO]: {
    ...CO_LITE_CONSTRAINTS,
    custom_lel: CUSTOM_LEL_STANDARD,
    vol_for_50_lel_ch4: [],
    lel_CF_Index: SENSOR_TO_CF_OPTIONS[SensorType.CO],
    bump_test_threshold: { default: 40, ranges: [[40, 100]], scale: 1, unit: '%' },
    features: {
      has_twa_stel: true,
      enable_unit_selection: false,
      enable_auto_fresh: true,
      enable_correction_factor: false,
      custom_lel_editable: false,
      enable_bump_test_threshold: true,
    },
  },
  [SensorType.H2S]: {
    ...H2S_LITE_CONSTRAINTS,
    custom_lel: CUSTOM_LEL_STANDARD,
    vol_for_50_lel_ch4: [],
    lel_CF_Index: SENSOR_TO_CF_OPTIONS[SensorType.H2S],
    bump_test_threshold: { default: 40, ranges: [[40, 100]], scale: 1, unit: '%' },
    features: {
      has_twa_stel: true,
      enable_unit_selection: false,
      enable_auto_fresh: true,
      enable_correction_factor: false,
      custom_lel_editable: false,
      enable_bump_test_threshold: true,
    },
  },
  // CO (Alpha) - CO(DD)와 동일한 제약조건
  [SensorType.CO_ALPHA]: {
    ...CO_LITE_CONSTRAINTS,
    custom_lel: CUSTOM_LEL_STANDARD,
    vol_for_50_lel_ch4: [],
    lel_CF_Index: SENSOR_TO_CF_OPTIONS[SensorType.CO_ALPHA],
    bump_test_threshold: { default: 40, ranges: [[40, 100]], scale: 1, unit: '%' },
    features: {
      has_twa_stel: true,
      enable_unit_selection: false,
      enable_auto_fresh: true,
      enable_correction_factor: false,
      custom_lel_editable: false,
      enable_bump_test_threshold: true,
    },
  },
  // H2S (Alpha) - H2S(DD)와 동일한 제약조건
  [SensorType.H2S_ALPHA]: {
    ...H2S_LITE_CONSTRAINTS,
    custom_lel: CUSTOM_LEL_STANDARD,
    vol_for_50_lel_ch4: [],
    lel_CF_Index: SENSOR_TO_CF_OPTIONS[SensorType.H2S_ALPHA],
    bump_test_threshold: { default: 40, ranges: [[40, 100]], scale: 1, unit: '%' },
    features: {
      has_twa_stel: true,
      enable_unit_selection: false,
      enable_auto_fresh: true,
      enable_correction_factor: false,
      custom_lel_editable: false,
      enable_bump_test_threshold: true,
    },
  },
  [SensorType.LEL_LPC]: {
    ...LEL_CONSTRAINTS,
    custom_lel: CUSTOM_LEL_STANDARD,
    vol_for_50_lel_ch4: Object.values(VOL_FOR_50_LEL_CH4_OPTIONS).map(option => ({
      value: option.value,
      label: option.label,
    })),
    lel_CF_Index: SENSOR_TO_CF_OPTIONS[SensorType.LEL_LPC],
    bump_test_threshold: { default: 40, ranges: [[40, 100]], scale: 1, unit: '%' },
    features: {
      has_twa_stel: false,
      enable_unit_selection: false,
      enable_auto_fresh: true,
      enable_correction_factor: true,
      custom_lel_editable: true,
      enable_bump_test_threshold: true,
    },
  },
  [SensorType.CO2_CRANBERRY]: {
    ...CO2_LITE_CONSTRAINTS,
    custom_lel: CUSTOM_LEL_STANDARD,
    vol_for_50_lel_ch4: [],
    lel_CF_Index: SENSOR_TO_CF_OPTIONS[SensorType.CO2_CRANBERRY],
    unit: [GAS_CONC_UNIT_OPTIONS[GasConcUnit.PPM], GAS_CONC_UNIT_OPTIONS[GasConcUnit.VOL]],
    bump_test_threshold: { default: 40, ranges: [[40, 100]], scale: 1, unit: '%' },
    features: {
      has_twa_stel: true,
      enable_unit_selection: true,
      enable_auto_fresh: false, // CO2-IR: auto fresh air cal at startup shall always be off
      enable_correction_factor: false,
      custom_lel_editable: false,
      enable_bump_test_threshold: true,
    },
  },
  [SensorType.CO2_CRANBERRY_VOL]: {
    ...CO2_VOL_CONSTRAINTS,
    custom_lel: CUSTOM_LEL_STANDARD,
    vol_for_50_lel_ch4: [],
    lel_CF_Index: SENSOR_TO_CF_OPTIONS[SensorType.CO2_CRANBERRY_VOL],
    unit: [GAS_CONC_UNIT_OPTIONS[GasConcUnit.PPM], GAS_CONC_UNIT_OPTIONS[GasConcUnit.VOL]],
    bump_test_threshold: { default: 40, ranges: [[40, 100]], scale: 1, unit: '%' },
    features: {
      has_twa_stel: true,
      enable_unit_selection: true,
      enable_auto_fresh: false, // CO2-IR: auto fresh air cal at startup shall always be off
      enable_correction_factor: false,
      custom_lel_editable: false,
      enable_bump_test_threshold: true,
    },
  },
  [SensorType.O2LF]: {
    ...O2LF_LITE_CONSTRAINTS,
    custom_lel: CUSTOM_LEL_STANDARD,
    vol_for_50_lel_ch4: [],
    lel_CF_Index: SENSOR_TO_CF_OPTIONS[SensorType.O2LF],
    bump_test_threshold: { default: 40, ranges: [[40, 40]], scale: 1, unit: '%' }, // O2LF는 40으로 고정
    features: {
      has_twa_stel: false,
      enable_unit_selection: false,
      enable_auto_fresh: true,
      enable_correction_factor: false,
      custom_lel_editable: false,
      enable_bump_test_threshold: false, // O2LF는 40으로 고정
    },
  },
  [SensorType.LEL_1Series]: {
    ...LEL_CONSTRAINTS,
    custom_lel: CUSTOM_LEL_STANDARD,
    vol_for_50_lel_ch4: Object.values(VOL_FOR_50_LEL_CH4_OPTIONS).map(option => ({
      value: option.value,
      label: option.label,
    })),
    lel_CF_Index: SENSOR_TO_CF_OPTIONS[SensorType.LEL_1Series],
    bump_test_threshold: { default: 40, ranges: [[40, 100]], scale: 1, unit: '%' },
    features: {
      has_twa_stel: false,
      enable_unit_selection: false,
      enable_auto_fresh: true,
      enable_correction_factor: true,
      custom_lel_editable: false,
      enable_bump_test_threshold: true,
    },
  },
  [SensorType.LEL_IR]: {
    ...LEL_CONSTRAINTS,
    custom_lel: CUSTOM_LEL_IR_VARIANT,
    vol_for_50_lel_ch4: Object.values(VOL_FOR_50_LEL_CH4_OPTIONS).map(option => ({
      value: option.value,
      label: option.label,
    })),
    lel_CF_Index: SENSOR_TO_CF_OPTIONS[SensorType.LEL_IR],
    bump_test_threshold: { default: 40, ranges: [[40, 100]], scale: 1, unit: '%' },
    features: {
      has_twa_stel: false,
      enable_unit_selection: false,
      enable_auto_fresh: false, // CH4-IR: auto fresh air cal at startup shall always be off
      enable_correction_factor: true,
      custom_lel_editable: false,
      enable_bump_test_threshold: true,
    },
  },
  [SensorType.NO_SENSOR]: {
    ...NO_SENSOR_CONSTRAINTS,
    custom_lel: CUSTOM_LEL_IR_VARIANT,
    vol_for_50_lel_ch4: [],
    lel_CF_Index: SENSOR_TO_CF_OPTIONS[SensorType.NO_SENSOR],
    bump_test_threshold: { default: 40, ranges: [[40, 100]], scale: 1, unit: '%' },
    features: {
      has_twa_stel: false,
      enable_unit_selection: false,
      enable_auto_fresh: false,
      enable_correction_factor: false,
      custom_lel_editable: false,
      enable_bump_test_threshold: false,
    },
  },
};

/**
 * 센서 타입별 제약 조건 가져오기
 */
export function getSensorConstraints(sensorType: SensorType) {
  return SENSOR_CONSTRAINTS[sensorType];
}

/**
 * 센서 타입 정보 가져오기 (기능 플래그 포함)
 * sensors.ts의 기본 정보와 constraints의 기능 플래그를 결합
 */
export const getSensorTypeInfo = (sensorType: SensorType): SensorTypeInfo & SensorFeatureFlags => {
  const baseInfo = SENSOR_TYPE_INFO[sensorType] ?? {
    index: -1,
    label: 'Unknown',
  };
  const constraints = getSensorConstraints(sensorType);
  const features = constraints?.features ?? {
    has_twa_stel: false,
    enable_unit_selection: false,
    enable_auto_fresh: false,
    enable_correction_factor: false,
    custom_lel_editable: false,
    enable_bump_test_threshold: false,
  };

  return {
    ...baseInfo,
    ...features,
  };
};

/**
 * Lite 센서 기본값 타입
 */
interface LiteSensorDefaultValues {
  gas_conc_unit: GasConcUnitOption;
  calibration_gas_conc: number;
  calibration_interval: number;
  bump_interval: number;
  low_alarm: number;
  high_alarm: number;
  twa_alarm: number;
  stel_alarm: number;
  twa_period: number;
  stel_period: number;
  sensor_disabled: boolean;
  auto_fresh_air_cal: boolean;
  custom_lel: number;
  lel_CF_Index: CF_option;
  vol_for_50_lel_ch4: VolFor50LelCh4Option;
  bump_test_threshold: number;
}

/**
 * Lite 센서별 기본값 생성 함수
 * @param sensorType - 센서 타입
 * @returns 센서별 설정 기본값 (UI 표시용 - scale 적용됨)
 */
export const getLiteSensorDefaultValues = (sensorType: SensorType): LiteSensorDefaultValues => {
  const constraints = SENSOR_CONSTRAINTS[sensorType];
  if (!constraints) {
    return {
      gas_conc_unit: GAS_CONC_UNIT_OPTIONS[GasConcUnit.PPM],
      calibration_gas_conc: 0,
      calibration_interval: 0,
      bump_interval: 0,
      low_alarm: 0,
      high_alarm: 0,
      twa_alarm: 0,
      stel_alarm: 0,
      twa_period: 0,
      stel_period: 0,
      sensor_disabled: false,
      auto_fresh_air_cal: false, // DISABLED: 항상 false로 설정됨 (2025-12-03)
      custom_lel: 1,
      lel_CF_Index: SENSOR_TO_CF_OPTIONS[SensorType.NO_SENSOR]?.[0] || { value: 0, label: '' },
      vol_for_50_lel_ch4: VOL_FOR_50_LEL_CH4_OPTIONS[VolFor50LelCh4.US],
      bump_test_threshold: 40,
    };
  }

  const unitValue = constraints.gas_conc_unit || GasConcUnit.PPM;
  return {
    gas_conc_unit: GAS_CONC_UNIT_OPTIONS[unitValue],
    calibration_gas_conc:
      (constraints.calibration_gas_conc?.default || 0) /
      (constraints.calibration_gas_conc?.scale || 1),
    calibration_interval: constraints.calibration_interval?.default || 0,
    bump_interval: constraints.bump_interval?.default || 0,
    low_alarm: (constraints.low_alarm?.default || 0) / (constraints.low_alarm?.scale || 1),
    high_alarm: (constraints.high_alarm?.default || 0) / (constraints.high_alarm?.scale || 1),
    twa_alarm: (constraints.twa_alarm?.default || 0) / (constraints.twa_alarm?.scale || 1),
    stel_alarm: (constraints.stel_alarm?.default || 0) / (constraints.stel_alarm?.scale || 1),
    twa_period: constraints.twa_period?.default || 0,
    stel_period: constraints.stel_period?.default || 0,
    sensor_disabled: sensorType === SensorType.NO_SENSOR,
    auto_fresh_air_cal: false, // DISABLED: 항상 false로 설정됨 (2025-12-03) - 기존: constraints.auto_fresh === 1
    custom_lel:
      (constraints.custom_lel?.default || GAS_CONC_SCALE) / (constraints.custom_lel?.scale || 1),
    lel_CF_Index:
      Array.isArray(constraints.lel_CF_Index) && constraints.lel_CF_Index.length > 0
        ? constraints.lel_CF_Index[0]
        : { value: 0, label: '' },
    vol_for_50_lel_ch4:
      Array.isArray(constraints.vol_for_50_lel_ch4) && constraints.vol_for_50_lel_ch4.length > 0
        ? constraints.vol_for_50_lel_ch4[0]
        : VOL_FOR_50_LEL_CH4_OPTIONS[VolFor50LelCh4.US],
    bump_test_threshold: constraints.bump_test_threshold?.default || 40,
  };
};
